// Inbox › Calls in Blueprint (calls round; BL-189): PageHead under the Inbox's Calls and Texts and email
// tabs, its line today's calls and the week's booking rate, said once; its one action the booking rate,
// a part of Calls (and back). Under it the calls as CallStrip lists (CallsLists.tsx), and for owners and
// managers the recordings to match; or the booking rate. An open call is its own page (OpenCall.tsx).
// What the screen holds and reads is use-calls-screen.ts's, the same as Riverstone's (Calls.tsx).
import { Button, Toast } from '@flowology/ui-web/fl';
import { PageHead, SectionSays } from '@flowology/ui-web/patterns';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { talkTabs } from '../inbox/TalkTabs.tsx';
import { BookingRateSection } from './BookingRateSection.tsx';
import { CallsLists } from './CallsLists.tsx';
import { daySummary } from './calls-lists-model.ts';
import { MatchRecordings } from './MatchRecordings.tsx';
import { OpenCall } from './OpenCall.tsx';
import { useMyRate } from './use-booking-rate.ts';
import { useCallsScreen } from './use-calls-screen.ts';
import './calls-bp.css';

/** The head's line: today's calls (on the calls), then the week's rate, each said once. */
export function callsLede(day: string | null, rate: string | null): string | undefined {
  const line = [day, rate && `${rate}.`].filter(Boolean).join(' ');
  return line || undefined;
}

export function CallsPage({
  nav,
  callId,
  onOpenCall,
  onBoard,
}: {
  nav: NavProps;
  callId: string | null;
  onOpenCall: (id: string | null) => void;
  /** The booking form's way to the board (it offers it only while the company mirrors). */
  onBoard: () => void;
}) {
  const s = useCallsScreen({ nav, callId });
  const rate = useMyRate(s.today, s.everyone, s.rateVersion);
  const crumb = usePageCrumb(nav);
  const toast = s.toast ? <Toast>{s.toast}</Toast> : null;

  if (callId)
    return (
      <div className="app calls-bp-call">
        <main className="calls-page bp-ib">
          <OpenCall s={s} nav={nav} callId={callId} onOpenCall={onOpenCall} onBoard={onBoard} />
        </main>
        {toast}
      </div>
    );

  const tabs = talkTabs(nav, s.backs.calls?.length ?? null);
  const lede = callsLede(s.part === 'calls' && s.day ? daySummary(s.day.total, s.day.booked) : null, rate);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <PageHead
          {...(crumb ? { crumb } : {})}
          title="Calls"
          {...(lede ? { lede } : {})}
          {...(tabs ? { tabs } : {})}
          actions={
            <Button onPress={() => s.setPart((p) => (p === 'rate' ? 'calls' : 'rate'))}>
              {s.part === 'rate' ? 'Back to the calls' : 'Booking rate'}
            </Button>
          }
        />
        {s.part === 'rate' ? (
          <BookingRateSection today={s.today} everyone={s.everyone} />
        ) : (
          <CallsLists
            today={s.recent}
            loading={
              <SectionSays
                status={s.recentFailed ? 'failed' : 'loading'}
                what="today’s calls"
                failedText="Couldn’t load today’s calls."
                onRetry={s.retryRecent}
              />
            }
            backs={s.backs}
            whenOf={s.whenOf}
            canClaim={s.held.includes('customers.edit')}
            onOpen={(id) => onOpenCall(id)}
            onOlder={s.olderBacks}
            onBacksChanged={s.retryBacks}
            {...(s.everyone ? { side: <MatchRecordings whenOf={s.whenOf} toast={s.setToast} /> } : {})}
          />
        )}
      </main>
      {toast}
    </div>
  );
}
